// Implements: F-PRV-002, F-PRV-007
/**
 * What the mockup page's viewer reads: the mockup's current version and that version's source.
 *
 * The current version comes from the ONE read of the mockup the whole page shares
 * (`mockupDetailKey`, F-MCK-003): no second request, and a finished turn that invalidates the detail
 * moves the viewer with the tree. The pointer only ever names a COMPLETE version (the writer sets it
 * when a generation succeeded), so adopting it is adopting a finished version (F-PRV-002). Until
 * that read has answered, or when it refused (an unknown mockup), there is no viewer at all.
 *
 * The source is read per version, under a key that names it; the container shows it only while
 * that version is the one in the frame, so a late answer for an older version is never displayed.
 * A failed read is not retried by the query (the shared client is `retry: false`; `httpClient` owns
 * the retries of a 5xx): the person asks for another read with the retry control.
 */

import { useQuery } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey } from "../../../shared/api/queryKeys";
import type { MockupDetailView } from "../../../types/api";
import { fetchVersionSource } from "../api/previewApi";
import type { VersionSource } from "../api/previewApi";

export type SourceStatus = "loading" | "error" | "ready";

export type MockupPreview =
  /** The mockup is still being read, or the server refused it: nothing to show. */
  | { readonly status: "unavailable" }
  | {
      readonly status: "ready";
      /** `null` when the mockup has no version yet. */
      readonly versionId: string | null;
      readonly source: VersionSource | undefined;
      readonly sourceStatus: SourceStatus;
      readonly retrySource: () => void;
    };

export function versionSourceKey(
  mockupId: string,
  versionId: string,
): readonly ["preview", "source", string, string] {
  return ["preview", "source", mockupId, versionId];
}

/**
 * `shownVersionId` asks for another version than the current one (UX-05: the version chip's
 * preview); `null` follows the mockup's pointer.
 */
export function useMockupPreview(mockupId: string, shownVersionId: string | null = null): MockupPreview {
  const detail = useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });
  const versionId = shownVersionId ?? detail.data?.current_version_id;
  const source = useQuery<VersionSource, ApiError>({
    queryKey: versionSourceKey(mockupId, versionId ?? ""),
    queryFn: () => fetchVersionSource(mockupId, versionId ?? ""),
    enabled: typeof versionId === "string",
  });
  // No answer yet, or a refusal (an unknown mockup has no data to show): no viewer.
  if (detail.data === undefined) return { status: "unavailable" };
  return {
    status: "ready",
    versionId: shownVersionId ?? detail.data.current_version_id,
    source: source.data,
    sourceStatus: source.isError ? "error" : source.data === undefined ? "loading" : "ready",
    retrySource: () => {
      void source.refetch();
    },
  };
}
